/**
 * React binding for the per-user chat preferences store.
 *
 * - Hydrates from Supabase on login (migrating legacy localStorage on first run)
 * - Optimistic updates with a debounced PATCH via the store
 * - `updatePreference(field, value)` for single fields, `updatePreferences(partial)` for batches
 */
import { useCallback, useEffect, useSyncExternalStore } from "react";
import type { UserChatPreferences } from "@shared/chatPreferences";
import {
  getChatPreferencesSnapshot,
  hydrateChatPreferences,
  isChatPreferencesHydrated,
  resetChatPreferences,
  subscribeChatPreferences,
  updateChatPreferences,
} from "@/lib/chatPreferencesStore";
import { useAuth } from "@/contexts/AuthContext";

export function useChatPreferences() {
  const { user } = useAuth();

  const preferences = useSyncExternalStore(
    subscribeChatPreferences,
    getChatPreferencesSnapshot,
    getChatPreferencesSnapshot
  );

  useEffect(() => {
    if (user?.id) {
      void hydrateChatPreferences(user.id);
    } else {
      resetChatPreferences();
    }
  }, [user?.id]);

  const updatePreference = useCallback(
    <K extends keyof UserChatPreferences>(field: K, value: UserChatPreferences[K]) => {
      updateChatPreferences({ [field]: value } as Partial<UserChatPreferences>);
    },
    []
  );

  return {
    preferences,
    isHydrated: isChatPreferencesHydrated(),
    updatePreference,
    updatePreferences: updateChatPreferences,
  };
}
